import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInvoice } from '../contexts/InvoiceContext';
import { INVOICE_STATUS_CONFIG } from '../../../types/accounting';
import { Invoice } from '../../../types/accounting';
import { useAppData } from '../../../providers/AppDataProvider';
import { authFetch } from '../../../utils/cloudFunctionsAuth';
import InvoiceViewModal from './InvoiceViewModal';
import { formatDateAU, isDateOverdue } from '../../../utils/dateUtils';

const CF_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';
const formatDateStr = formatDateAU;

interface CustomerInvoiceHistoryProps {
  contactId: string;
  contactName: string;
  onClose: () => void;
}

export function CustomerInvoiceHistory({ contactId, contactName, onClose }: CustomerInvoiceHistoryProps) {
  const { invoices, invoicesLoading, setInvoiceFilters, fetchInvoices } = useInvoice();
  const { customers } = useAppData();
  const navigate = useNavigate();
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  // Orphan detection & repair state
  const isOrphan = useMemo(() => {
    if (!contactId) return true;
    return !customers.some(c => c.id === contactId);
  }, [contactId, customers]);

  const [repairMode, setRepairMode] = useState<'link' | 'create' | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [repairing, setRepairing] = useState(false);
  const [repairSuccess, setRepairSuccess] = useState<string | null>(null);
  const [repairError, setRepairError] = useState<string | null>(null);

  const sortedCustomers = useMemo(() =>
    [...customers].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [customers]
  );

  const filteredCustomers = useMemo(() => {
    if (!searchTerm) return sortedCustomers.slice(0, 50);
    const lower = searchTerm.toLowerCase();
    return sortedCustomers.filter(c =>
      (c.name || '').toLowerCase().includes(lower) ||
      (c.email || '').toLowerCase().includes(lower) ||
      (c.company || '').toLowerCase().includes(lower)
    ).slice(0, 50);
  }, [searchTerm, sortedCustomers]);

  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');

  useEffect(() => {
    if (contactId) {
      setInvoiceFilters({ contactId, status: undefined });
      fetchInvoices({ contactId, status: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);

  const customerInvoices = invoices;
  const lifetimeSpend = customerInvoices
    .filter(inv => inv.status !== 'VOIDED')
    .reduce((sum, inv) => sum + (inv.total || 0), 0);
  const totalOutstanding = customerInvoices
    .filter(inv => inv.status === 'APPROVED' || inv.status === 'SENT')
    .reduce((sum, inv) => sum + (inv.amountDue || 0), 0);
  const overdueInvoices = customerInvoices.filter(
    inv => (inv.status === 'APPROVED' || inv.status === 'SENT') && isDateOverdue(inv.dueDate)
  );
  const overdueAmount = overdueInvoices.reduce((sum, inv) => sum + (inv.amountDue || 0), 0);
  const statusCounts: Record<string, number> = {};
  customerInvoices.forEach(inv => {
    statusCounts[inv.status] = (statusCounts[inv.status] || 0) + 1;
  });

  const handleRepair = async (action: 'link' | 'create', linkToCustomerId?: string) => {
    setRepairing(true);
    setRepairError(null);
    try {
      const decision: any = { contactName, action };
      if (action === 'link' && linkToCustomerId) {
        decision.linkToCustomerId = linkToCustomerId;
      }
      const res = await authFetch(`${CF_BASE}/repairOrphanedContactsHttp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decisions: [decision] }),
      });
      const data = await res.json();
      if (data.ok) {
        const r = data.results?.[0];
        setRepairSuccess(
          action === 'link'
            ? `Linked to existing customer. ${r?.documentsUpdated || 0} document(s) updated.`
            : `New customer created. ${r?.documentsUpdated || 0} document(s) updated.`
        );
        setRepairMode(null);
      } else {
        setRepairError(data.error || 'Repair failed');
      }
    } catch (e: any) {
      setRepairError(e?.message || 'Repair failed');
    } finally {
      setRepairing(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header border-0 pt-5">
        <div className="d-flex align-items-center justify-content-between w-100">
          <div>
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bold fs-3 mb-1">{contactName}</span>
              <span className="text-muted mt-1 fw-semibold fs-7">Invoice History & Lifetime Spend</span>
            </h3>
          </div>
          <div className="d-flex gap-2">
            {!isOrphan && !repairSuccess && contactId && (
              <button
                className="btn btn-sm btn-light-primary"
                onClick={() => {
                  onClose();
                  navigate(`/contacts?id=${contactId}`);
                }}
              >
                <i className="bi bi-person me-1" />View Contact
              </button>
            )}
            <button className="btn btn-sm btn-light" onClick={onClose}>
              <i className="bi bi-x-lg me-1" />Close
            </button>
          </div>
        </div>
      </div>

      {/* Orphan warning banner */}
      {isOrphan && !repairSuccess && (
        <div className="card-body py-3 border-bottom">
          <div className="alert alert-warning mb-0 py-3">
            <div className="d-flex align-items-start gap-2">
              <i className="bi bi-exclamation-triangle-fill text-warning fs-4 mt-1" />
              <div className="flex-grow-1">
                <h6 className="fw-bold mb-1">Orphaned Contact</h6>
                <p className="mb-2 text-muted fs-7">
                  This contact's invoices aren't linked to any customer record. Choose how to fix:
                </p>

                {!repairMode && (
                  <div className="d-flex gap-2">
                    <button className="btn btn-sm btn-primary" style={{ padding: '4px 12px' }} onClick={() => setRepairMode('link')}>
                      <i className="bi bi-link-45deg me-1" />Link to Existing Customer
                    </button>
                    <button className="btn btn-sm btn-success" style={{ padding: '4px 12px' }} onClick={() => handleRepair('create')} disabled={repairing}>
                      {repairing ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-plus-circle me-1" />}
                      Create New Customer
                    </button>
                  </div>
                )}

                {repairMode === 'link' && (
                  <div style={{ maxWidth: '400px' }}>
                    <div className="mb-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search customers by name, email, or company..."
                        value={selectedCustomerId ? (sortedCustomers.find(c => c.id === selectedCustomerId)?.name || '') : searchTerm}
                        onChange={e => {
                          setSearchTerm(e.target.value);
                          setSelectedCustomerId('');
                        }}
                        onFocus={() => {
                          if (selectedCustomerId) {
                            setSearchTerm('');
                            setSelectedCustomerId('');
                          }
                        }}
                      />
                      {!selectedCustomerId && (
                        <div className="border rounded mt-1" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                          {filteredCustomers.map(c => (
                            <button
                              key={c.id}
                              className="dropdown-item py-2 px-3"
                              style={{ fontSize: '0.85rem' }}
                              onClick={() => {
                                setSelectedCustomerId(c.id || '');
                                setSearchTerm('');
                              }}
                            >
                              <span className="fw-semibold">{c.name}</span>
                              {c.email && <span className="text-muted ms-2 fs-8">{c.email}</span>}
                            </button>
                          ))}
                          {filteredCustomers.length === 0 && (
                            <span className="d-block text-muted py-2 px-3 fs-8">No customers found</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-primary"
                        style={{ padding: '4px 12px' }}
                        onClick={() => handleRepair('link', selectedCustomerId)}
                        disabled={!selectedCustomerId || repairing}
                      >
                        {repairing ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                        Confirm Link
                      </button>
                      <button className="btn btn-sm btn-light" style={{ padding: '4px 12px' }} onClick={() => { setRepairMode(null); setSearchTerm(''); setSelectedCustomerId(''); }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {repairError && (
                  <div className="alert alert-danger py-2 mt-2 mb-0">
                    <i className="bi bi-exclamation-triangle me-1" />{repairError}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Repair success banner */}
      {repairSuccess && (
        <div className="card-body py-3 border-bottom">
          <div className="alert alert-success mb-0 py-3">
            <i className="bi bi-check-circle me-2" />{repairSuccess}
          </div>
        </div>
      )}

      {invoicesLoading ? (
        <div className="card-body py-10 text-center">
          <span className="spinner-border text-primary" role="status" />
          <p className="text-muted mt-3">Loading customer history...</p>
        </div>
      ) : (
        <>
          <div className="card-body py-4 border-bottom">
            <div className="row g-4">
              <div className="col-md-3 col-6">
                <div className="d-flex flex-column">
                  <span className="text-muted fs-7">Lifetime Spend</span>
                  <span className="fs-3 fw-bold text-success">{formatCurrency(lifetimeSpend)}</span>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="d-flex flex-column">
                  <span className="text-muted fs-7">Total Invoiced</span>
                  <span className="fs-3 fw-bold text-primary">{formatCurrency(lifetimeSpend)}</span>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="d-flex flex-column">
                  <span className="text-muted fs-7">Outstanding</span>
                  <span className="fs-3 fw-bold text-warning">{formatCurrency(totalOutstanding)}</span>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="d-flex flex-column">
                  <span className="text-muted fs-7">Overdue</span>
                  <span className={`fs-3 fw-bold ${overdueAmount > 0 ? 'text-danger' : 'text-muted'}`}>
                    {formatCurrency(overdueAmount)}
                    {overdueInvoices.length > 0 && (
                      <span className="badge badge-danger ms-2">{overdueInvoices.length}</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="card-body py-4 border-bottom">
            <div className="d-flex flex-wrap gap-3">
              {Object.entries(statusCounts).map(([status, count]) => {
                if (count === 0) return null;
                const config = INVOICE_STATUS_CONFIG[status as keyof typeof INVOICE_STATUS_CONFIG];
                if (!config) return null;
                return (
                  <div key={status} className="d-flex align-items-center gap-2">
                    <span className={`badge ${config.badgeClass}`}>{config.label}</span>
                    <span className="fw-semibold">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card-body py-3">
            {customerInvoices.length === 0 ? (
              <div className="text-center py-10">
                <i className="bi bi-file-earmark-check text-muted fs-1"></i>
                <p className="text-muted mt-3">No invoices found for this customer.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3">
                  <thead>
                    <tr className="fw-bold text-muted fs-7">
                      <th>Invoice #</th>
                      <th>Date</th>
                      <th>Due Date</th>
                      <th>Total</th>
                      <th>Paid</th>
                      <th>Due</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerInvoices.map((invoice) => {
                      const config = INVOICE_STATUS_CONFIG[invoice.status];
                      const overdue =
                        (invoice.status === 'APPROVED' || invoice.status === 'SENT') &&
                        (invoice.amountDue || 0) > 0 &&
                        isDateOverdue(invoice.dueDate);

                      return (
                        <tr key={invoice.id}>
                          <td>
                            <button
                              className="btn btn-link p-0 text-start text-hover-primary fw-semibold"
                              onClick={() => setSelectedInvoice(invoice)}
                              title="View / Download"
                            >
                              {invoice.invoiceNumber}
                            </button>
                          </td>
                          <td className="text-muted">{formatDateStr(invoice.date)}</td>
                          <td className={overdue ? 'text-danger fw-semibold' : ''}>
                            {formatDateStr(invoice.dueDate)}
                            {overdue && <i className="bi bi-exclamation-circle-fill text-danger ms-1"></i>}
                          </td>
                          <td>{formatCurrency(invoice.total)}</td>
                          <td className="text-success">{formatCurrency(invoice.amountPaid || 0)}</td>
                          <td className={(invoice.amountDue || 0) > 0 ? 'fw-bold' : 'text-muted'}>
                            {formatCurrency(invoice.amountDue || 0)}
                          </td>
                          <td>
                            <span className={`badge ${config.badgeClass}`}>{config.label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <InvoiceViewModal
            show={!!selectedInvoice}
            invoice={selectedInvoice}
            onHide={() => setSelectedInvoice(null)}
          />
        </>
      )}
    </div>
  );
}

export default CustomerInvoiceHistory;
